// client/src/pages/Services.js
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api/axiosInstance";

/**
 * LANDING PAGE SERVICES
 * CLEAN + CONTROLLED FILTERS
 */

const PLATFORM_FILTERS = [
  "ALL",
  "INSTAGRAM",
  "FACEBOOK",
  "YOUTUBE",
  "TWITTER",
  "TELEGRAM",
  "SPOTIFY",
  "OTHER",
];

const normalizePlatform = (value = "") => {
  const v = value.toLowerCase();
  if (v.includes("instagram")) return "INSTAGRAM";
  if (v.includes("facebook")) return "FACEBOOK";
  if (v.includes("youtube")) return "YOUTUBE";
  if (v.includes("twitter")) return "TWITTER";
  if (v.includes("telegram")) return "TELEGRAM";
  if (v.includes("spotify")) return "SPOTIFY";
  return "OTHER";
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [platform, setPlatform] = useState("ALL");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openService, setOpenService] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get("/services-public")
      .then((res) => setServices(res.data || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredServices = useMemo(() => {
    return services.filter((s) => {
      const normalized = normalizePlatform(s.platform);
      const platformMatch =
        platform === "ALL" || normalized === platform;

      const q = search.toLowerCase();
      const searchMatch =
        !q ||
        s.name.toLowerCase().includes(q) ||
        String(s.providerServiceId).includes(q);

      return platformMatch && searchMatch;
    });
  }, [services, platform, search]);

  return (
    <div className="min-h-screen bg-[#05040f] text-white">
      
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20">
        <h1 className="text-4xl font-bold text-center mb-2">
          SMSOrbit <span className="text-purple-400">Services</span>
        </h1>
        <p className="text-center text-gray-400 mb-10">
          Browse all available services. Login to place an order.
        </p>

        {/* FILTER BAR */}
        <div className="flex flex-wrap gap-3 items-center mb-6">
          {PLATFORM_FILTERS.map((p) => (
            <button
              key={p}
              onClick={() => setPlatform(p)}
              className={`px-4 py-1.5 rounded-full text-sm border transition ${
                platform === p
                  ? "bg-purple-600 border-purple-500"
                  : "bg-white/5 border-white/10 hover:bg-white/10"
              }`}
            >
              {p}
            </button>
          ))}

          <input
            className="ml-auto w-full md:w-72 px-4 py-2 rounded-xl bg-black/40 border border-white/10 outline-none"
            placeholder="Search by service name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* TABLE */}
        <div className="rounded-2xl bg-[#090912] border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="p-3 text-left">ID</th>
                <th className="p-3 text-left">Service</th>
                <th className="p-3 text-left">Platform</th>
                <th className="p-3 text-right">Min</th>
                <th className="p-3 text-right">Max</th>
                <th className="p-3 text-right">Price / 1k</th>
                <th className="p-3 text-center">Details</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="p-6 text-center text-gray-400">
                    Loading services...
                  </td>
                </tr>
              ) : filteredServices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-6 text-center text-gray-400">
                    No services found
                  </td>
                </tr>
              ) : (
                filteredServices.map((s) => (
                  <tr
                    key={s._id}
                    className="border-t border-white/5 hover:bg-white/5"
                  >
                    <td className="p-3">{s.providerServiceId}</td>
                    <td className="p-3 font-semibold">{s.name}</td>
                    <td className="p-3">
                      {normalizePlatform(s.platform)}
                    </td>
                    <td className="p-3 text-right">{s.min}</td>
                    <td className="p-3 text-right">{s.max}</td>
                    <td className="p-3 text-right text-green-400">
                      ₹{Number(s.rate).toFixed(2)}
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => setOpenService(s)}
                        className="px-3 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-xs"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* DESCRIPTION MODAL */}
      <AnimatePresence>
        {openService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-[#0c0c18] border border-white/10 rounded-2xl p-6 max-w-lg w-full"
            >
              <h3 className="text-xl font-bold mb-3">
                {openService.name}
              </h3>

              <p className="text-sm text-gray-300 whitespace-pre-line">
                {openService.description || "No description available"}
              </p>

              <button
                onClick={() => setOpenService(null)}
                className="mt-6 px-6 py-2 bg-purple-600 rounded-xl hover:bg-purple-700"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
